<template>
  <div>
    <ShareLinkInvalidMessage v-if="showLinkExpiredMessage">
      This link is either invalid, or the strategy is no longer shared by its owner. Ask the owner of the strategy for a new shareable link.
    </ShareLinkInvalidMessage>
    <template v-else>
      <SharedPageBanner v-if="!loadingContent" v-bind="shareBannerProps" />
      <br>
      <div class="container">
        <CopyTrackWrapper
          :type="contentTrackingType"
          :title="copyTrackerParams.title"
          :uuid="copyTrackerParams.uuid"
          :demo="copyTrackerParams.demo">
          <CopyTrackWrapper
            :title="copyTrackerParams.title"
            :uuid="copyTrackerParams.uuid"
            type="strategy_banner">
            <StrategyBanner v-if="bannerProps" v-bind="bannerProps"/>
          </CopyTrackWrapper>
          <div class="row">
            <div class="col-sm-8">
              <CopyTrackWrapper
                :title="copyTrackerParams.title"
                :uuid="copyTrackerParams.uuid"
                type="strategy_notes">
                <StrategyNotes
                  v-if="savedStrategyInfo.notes"
                  :editable="false"
                  :student-name="savedStrategyInfo.studentName"
                  :user-name="savedStrategyInfo.authorName"
                  :notes="savedStrategyInfo.notes" />
              </CopyTrackWrapper>
            </div>
          </div>

          <div class="row">
            <div class="col-sm-8">
              <StrategyResourceList v-if="resourcesProps" v-bind="resourcesProps"/>
              <StrategyPanels v-if="implementationProps" v-bind="implementationProps"/>
              <StrategyPanels v-if="examplesProps" v-bind="examplesProps"/>
              <ResearchCitationPanels v-if="researchCitationProps" :research-citations="researchCitationProps" />
            </div>
          </div>

          <StrategyRelated v-if="relatedProps" v-bind="relatedProps"/>

          <SubmitFeedback v-if="feedbackProps" v-bind="feedbackProps" />
          <TermsOfUseFooter />
        </CopyTrackWrapper>
      </div>
    </template>
  </div>
</template>

<script>
import { getSharedStrategy } from '@/api/strategies'
import { setTitle } from '@/util/title'

import { strategyPageMixin } from '@/components/strategies/strategy_mixins.js'

import StrategyNotes from '@/components/strategies/details/StrategyNotes'
import { CONTENT_TYPES } from '@/util/access_visibility.js'

import SharedPageBanner from '@/components/SharedPageBanner'
import ShareLinkInvalidMessage from '@/components/library/ShareLinkInvalidMessage'
import TermsOfUseFooter from '@/components/library/TermsOfUseFooter'

export default {
  name: 'SharedStrategyDetailPage',
  mixins: [strategyPageMixin],
  components: {
    StrategyNotes,
    SharedPageBanner,
    ShareLinkInvalidMessage,
    TermsOfUseFooter
  },
  props: {
    uuid: {
      type: String,
      required: true
    },
    libraryGoalUuid: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      copyTrackerParams: {},
      feedbackProps: null,
      shareBannerProps: null,
      sourceStrategy: {},
      savedStrategyInfo: {},
      loadingContent: false,
      showLinkExpiredMessage: false,
      contentTrackingType: CONTENT_TYPES.SHARED_STRATEGY
    }
  },
  computed: {
    shareLink() {
      return (
        window.location.origin +
        this.$router.resolve({
          name: 'SharedStrategyDetailPage',
          params: { uuid: this.uuid }
        }).href
      )
    }
  },
  watch: {
    $route(to, from) {
      this.fetchContent()
    }
  },
  created() {
    this.fetchContent()
  },
  eventContext() {
    return {
      strategy_uuid: this.sourceStrategy.uuid,
      strategy_title: this.sourceStrategy.title,
      student_uuid: this.savedStrategyInfo.studentUuid,

      // deprecated
      uuid: this.uuid,
      title: this.sourceStrategy.title
    }
  },
  methods: {
    async fetchContent() {
      this.loadingContent = true
      try {
        let resp = await getSharedStrategy(this.uuid)
        this.parseStrategy(resp)
      } catch (err) {
        console.error(err)
        this.showLinkExpiredMessage = true
      } finally {
        this.loadingContent = false
      }
    },
    parseStrategy(strategyData) {
      setTitle(`${strategyData.source_strategy.title} | UDL Strategies`)
      this.sourceStrategy = strategyData.source_strategy
      this.savedStrategyInfo.studentName = strategyData.student_name
      this.savedStrategyInfo.studentUuid = strategyData.student_uuid
      this.savedStrategyInfo.authorName = strategyData.user_info.name
      this.savedStrategyInfo.notes = strategyData.notes

      this.setCommonProps(Object.assign({}, strategyData, strategyData.source_strategy), true)

      this.$sendEvent(
        'Viewed Shared Strategy Page',
        {
          uuid: strategyData.uuid,
          title: strategyData.source_strategy.title
        },
        {
          cu: strategyData.uuid,
          ct: strategyData.source_strategy.title
        }
      )

      this.copyTrackerParams = {
        uuid: strategyData.uuid,
        title: strategyData.source_strategy.title,
        demo: strategyData.source_strategy.demo
      }

      this.feedbackProps = {
        type: 'Strategy Detail Page',
        uuid: strategyData.uuid,
        title: strategyData.source_strategy.title
      }

      this.shareBannerProps = {
        contentTypeLabel: 'strategy',
        contentType: CONTENT_TYPES.SAVED_STRATEGY,
        shareLink: this.shareLink,
        userName: strategyData.user_info.name,
        lastUpdated: strategyData.updated_at,
        studentName: strategyData.student_name
      }
      this.loadingContent = false
    }
  }
}
</script>

<style>
</style>
